import { Column } from 'typeorm';
import { z } from 'zod';

import { Features } from '../types/features';
import { SubscriptionInterval } from '../types/subscription-interval';
import { SubscriptionPlanName } from '../types/subscription-plan';
import { SubscriptionStatus } from '../types/subscription-status';
import { TrialType } from '../types/trial-type';

import { IBaseEntity } from './base-entity.interface';
import { Organization, OrganizationId } from './organization.interface';

export const SubscriptionIdSchema = z.string().brand('SubscriptionId');
export type SubscriptionId = z.infer<typeof SubscriptionIdSchema> | string;

/**
 * A subscription of an organization.
 */
export interface Subscription extends IBaseEntity {
    /**
     * Unique identifier of the subscription
     */
    id: SubscriptionId;

    /**
     * Identifier of the organization this subscription belongs to
     */
    organizationId: OrganizationId;

    /**
     * Identifier of the related stripe subscription
     */
    stripeSubscriptionId?: string | null;

    /**
     * The type of plan for this subscription
     */
    plan: SubscriptionPlanName;

    /**
     * The current status of the subscription
     */
    status: SubscriptionStatus;

    /**
     * The amount of credits currently owned in the subscription
     */
    credits: number;

    /**
     * The number of allowed domains for this subscription
     */
    domains: number;

    /**
     * The features available in this subscription
     */
    features: Features;

    /**
     * Whether it's a full or trialed subscription
     */
    trialType?: TrialType | null;

    /**
     * The reoccurence interval of the subscription
     */
    interval?: SubscriptionInterval;

    /**
     * A Date in the future at which the subscription will automatically be canceled
     */
    cancelAt?: Date | null;

    /**
     * A Date in the past at which the subscription was canceled
     */
    canceledAt?: Date | null;

    /**
     * The Date at which the current trial ends
     */
    trialEnd?: Date | null;

    /**
     * Date for the start of the current subscription period
     */
    currentPeriodStart: Date;

    /**
     * Date for the end of the current subscription period
     */
    currentPeriodEnd?: Date | null;

    /**
     * The related organization entity
     */
    organization: Organization;
}
